class ModalHandler {
  constructor() {
    this.modalContainer = this.createModalContainer();
    this.currentOpenModal = null;
    this.triggerElement = null;
    this.focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    this.setupEventListeners();
  }

  createModalContainer() {
    const container = document.createElement('div');
    container.id = 'modal-container';
    container.innerHTML = `
      <div class="modal" role="dialog" aria-modal="true">
        <div class="modal__custom__content">
          <button type="button" class="modal__custom__close-button" aria-label="Close">
            <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 18 17">
              <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
            </svg>
          </button>
          <div class="modal__custom__body"></div>
        </div>
      </div>
    `;
    document.body.appendChild(container);
    return container;
  }

  setupEventListeners() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-modal-trigger]');
      if (trigger) {
        event.preventDefault();
        this.openModal(trigger);
      }
    });

    this.modalContainer.addEventListener('click', (event) => {
      // Only respond to left-clicks (button 0) or undefined (touch/keyboard events)
      // Ignore right-clicks (button 2) and middle-clicks (button 1)
      if (event.button !== undefined && event.button !== 0) return;
      
      if (event.target.classList.contains('modal__custom__close-button') || 
          event.target.closest('.modal__custom__close-button') ||
          event.target === this.modalContainer) {
        this.closeModal();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.currentOpenModal) {
        this.closeModal();
      }
      if (event.key === 'Tab' && this.currentOpenModal) {
        this.trapFocus(event);
      }
    });
  }

  openModal(trigger) {
    this.triggerElement = trigger;
    const modalId = trigger.getAttribute('data-modal-trigger');
    const modalContent = document.getElementById(modalId);
    
    if (!modalContent) {
      console.error(`Modal content with id "${modalId}" not found.`);
      return;
    }

    const modal = this.modalContainer.querySelector('.modal');
    modal.querySelector('.modal__custom__body').innerHTML = modalContent.innerHTML;
    
    // Load lazy images - swap data-src to src for images with lazy-modal-img class
    this.loadLazyImages(modal);
    
    this.modalContainer.classList.add('active');
    this.currentOpenModal = modal;
    document.body.style.overflow = 'hidden';

    // Set focus after a short delay to ensure content is rendered
    setTimeout(() => {
        const focusableElements = Array.from(modal.querySelectorAll(this.focusableElements))
        .filter(el => !el.disabled && el.offsetParent !== null);
        
        if (focusableElements.length > 0) {
        focusableElements[0].focus();
        } else {
        modal.querySelector('.modal__close-button').focus();
        }
    }, 100);

    this.announceToScreenReader('Modal opened');
  }

  closeModal() {
    this.modalContainer.classList.remove('active');
    this.currentOpenModal = null;
    document.body.style.overflow = '';
    
    if (this.triggerElement) {
      this.triggerElement.focus();
    }

    this.announceToScreenReader('Modal closed');
  }

  loadLazyImages(container) {
    // Find all images with data-src attribute and lazy-modal-img class
    const lazyImages = container.querySelectorAll('img.lazy-modal-img[data-src]');
    
    lazyImages.forEach(img => {
      // Set the src attribute to trigger image loading
      img.src = img.getAttribute('data-src');
      // Remove data-src attribute to avoid reprocessing
      img.removeAttribute('data-src');
    });
  }

trapFocus(event) {
  if (!this.currentOpenModal) return;

  // Get all focusable elements, including nested ones
  const focusableContent = Array.from(this.currentOpenModal.querySelectorAll(this.focusableElements))
    .filter(el => !el.disabled && el.offsetParent !== null); // Ensure element is visible

  if (focusableContent.length === 0) return;

  const firstElement = focusableContent[0];
  const lastElement = focusableContent[focusableContent.length - 1];

  // If we're not on a focusable element within the modal, focus on the first one
  if (!focusableContent.includes(document.activeElement)) {
    event.preventDefault();
    firstElement.focus();
    return;
  }

  // Handle tabbing
  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
}

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.classList.add('sr-only');
    announcement.textContent = message;
    document.body.appendChild(announcement);
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ModalHandler();
});